import { InfoBanner, InfoHeaderIcon } from "../components/Shared/InfoBanner";
import { Fragment, useContext, useEffect, useState } from "react";
import { AppContext } from "../../contexts/AppContext";
import { Colour, UserType } from "../Variables";
import { LoadingContent } from "../components/Shared/LoadingContent";
import { Card } from "../components/Shared/Card";
import { API_SpeculativeGetAll, SpeculativePositionModel } from "../../apis/ApisSpeculative";
import { RouteContext } from "../../contexts/RoutesContext";
import { API_GetApplicantExisting, API_PositionCreateOrRedirectCheck, ExistingApplicationModel } from "../../apis/ApisPositions";
import { useSearchParams } from "react-router-dom";
import { CreateApplicationCard } from "../components/Positions/CreateApplicationCard";
import { Loading } from "../components/Shared/Loading";
import { PositionLoginPromptPopup } from "../components/Positions/PositionLoginPromptPopup";

export function Speculative() {
    const app = useContext(AppContext);
    const routeContext = useContext(RouteContext);
    const [queryParameters] = useSearchParams();
    const [data, setData] = useState<SpeculativePositionModel[]>([]);
    const [selected, setSelected] = useState<SpeculativePositionModel>(null);
    const [selectedLoginPrompt, setSelectedLoginPrompt] = useState<SpeculativePositionModel>(null);
    const [existingData, setExistingData] = useState<ExistingApplicationModel[]>(null);
    const [existingToCopy, setExistingToCopy] = useState<number | null>(null);
    const [redirectChecking, setRedirectChecking] = useState<boolean>(false);
    const [loadingDetails, setLoadingDetails] = useState<boolean>(true);

    let aref = queryParameters.get("aref");
    let a = queryParameters.get("a"); // same as aref, some places direct as a instead
    let n = queryParameters.get("n");
    let id = queryParameters.get("id");

    useEffect(() => {

        if (app.userLoggedIn)
            routeContext.setRoutes([{ name: "Positions", route: "/" }, { name: "Speculative", route: null }]);
        else
            routeContext.setRoutes([{ name: "Speculative", route: null }]);

        loadData();
    }, []);

    useEffect(() => {
        if (!app.userLoggedIn) {
            setSelected(null);
            setSelectedLoginPrompt(null);
            setExistingData([]);
            setExistingToCopy(null);
            routeContext.setRoutes([{ name: "Positions", route: "/" }, { name: "Speculative", route: null }]);
        }
        if (!loadingDetails) {
            setLoadingDetails(true);
            loadData();
        }
    }, [app.userLoggedIn]);

    const loadData = async () => {
        var res = await API_SpeculativeGetAll(app.token);
        setData(res);

        if (app.userLoggedIn) {
            if (existingData == null || existingData.length == 0) {
                var existing = await API_GetApplicantExisting(aref);
                setExistingData(existing);
            }
        }
        else {
            setExistingData([]);
        }

        if (id != null) {
            var idMatch = res.find(x => x.ApplyingForID.toString() == id);
            if (idMatch != null)
                ApplyingSelect(res.find(x => x.ApplyingForID.toString() == id));
        }

        setLoadingDetails(false);
    }

    const ApplyingSelect = async (chosen: SpeculativePositionModel) => {
        setSelected(chosen);
        setSelectedLoginPrompt(chosen);
        routeContext.setRoutes([{ name: "Positions", route: "/" }, { name: "Speculative", route: `/Speculative${(app.token?.length ?? 0) > 0 ? "?token=" + app.token : ""}`, forceReload: true }, { name: chosen.CandidateTitle, route: null }]);
    }

    const CancelApplying = async () => {
        setSelected(null);
        setSelectedLoginPrompt(null);
        setExistingToCopy(null);
        routeContext.setRoutes([{ name: "Positions", route: "/" }, { name: "Speculative", route: null }]);
    }

    const Apply = async () => {
        if (!redirectChecking) {
            setRedirectChecking(true);
            window.scrollTo({
                top: 0,
                behavior: "smooth"
            });
            await API_PositionCreateOrRedirectCheck({ Aref: (aref?.length ?? 0) > 0 ? aref : a, ExistingToCopyID: existingToCopy, RefID: selected.ApplyingForID, returl: null, Token: app.token, IntranetReq: false, Name: n });
        }
    }

    let alreadyApplied = selected != null && existingData != null && existingData.find(x => x.ApplyingForID == selected.ApplyingForID)

    const completedLoginCallback = async (doLogic: boolean) => {
        if (!doLogic)
            return;

        setSelected(selectedLoginPrompt);

        function delay(ms: number) {
            return new Promise(resolve => setTimeout(resolve, ms));
        }

        if (!loadingDetails) {
            setLoadingDetails(true);
            loadData();
        }

        await delay(100);
        var existing = await API_GetApplicantExisting(aref);
        setExistingData(existing);

        if (existing == null || existing.length == 0) {
            await Apply();
        }
        else {
            await delay(100);
            document.getElementById("createApplicationCard")?.scrollIntoView();
        }
    }

    return (
        <>
            <InfoBanner
                header="Speculative"
                narrow={false}
                icon={InfoHeaderIcon.Speculative}
                info={`If you like the sound of working for ${app.firmName} but we don't have a vacancy that matches your experience, we would still like to hear from you. Our online application system allows you to register your details and CV with us for consideration within the relevant division in the future.
                        We maintain pools of talented candidates and may contact you when a relevant position arises. Please complete as much of the application as possible so that we can consider you for vacancies that suit your skills and experience. You will also be provided with the opportunity to sign up for automatic job alerts. `}
                subHeader=""
                theme={Colour.Secondary}>
            </InfoBanner>

            <PositionLoginPromptPopup onCompleteCallback={completedLoginCallback} applyingForID={selectedLoginPrompt?.ApplyingForID} candidateTitle={selectedLoginPrompt?.CandidateTitle} speculative={true} intranet={false} />

            {redirectChecking && <div className="alert alert-success"><p className="text-center"><b>Creating new application</b></p><Loading classes={"row col-12 text-center"} /></div>}

            {app.loggedInUser != null && app.loggedInUser.UserType == UserType.Candidate && selected != null && existingData != null && alreadyApplied == null && existingData.length > 0 &&
                <CreateApplicationCard ExistingApplications={existingData} setExistingToCopy={setExistingToCopy} existingToCopy={existingToCopy} />
            }

            <Card title={"Speculative applications"} headerTheme={Colour.Primary} headerClasses={`${selected != null ? "" : "mb-3"}`}>
                {selected == null &&
                    <LoadingContent loading={data == null} classes={""}>
                        <div className="row">
                            {data != null && data.map(x => <Fragment key={`SID_${x.ApplyingForID}`}>
                                <div className="col-sm-6" key={`SID_L_${x.ApplyingForID}`}>
                                    <b>{x.CandidateTitle}</b>
                                </div>
                                <div className="col-sm-6 mb-3" key={`SID_R_${x.ApplyingForID}`}>
                                    {app.userLoggedIn &&
                                        <button className="btn btn-secondary btn-sm btn-wider" onClick={c => { c.preventDefault(); ApplyingSelect(x); }}>
                                            {app.loggedInUser.UserType == UserType.Candidate ? "Apply" : "Put a candidate forward for this position"}
                                        </button>}
                                    {!app.userLoggedIn &&
                                        <button type="button" className="btn btn-alt" data-bs-toggle="modal" onClick={c => { setSelectedLoginPrompt(x); }} data-bs-target="#loginPromptModal">Apply</button>}
                                </div>
                            </Fragment>)}
                        </div>
                    </LoadingContent>
                }
                {selected != null &&
                    <div className="row">
                        <div className="col-12">Chosen speculative application <b>{selected.CandidateTitle}</b></div>

                        {selected != null && existingData != null && alreadyApplied != null && <div className="col-12"><div className="alert alert-warning mt-2">
                            You have already have applied for this position
                        </div></div>}
                    </div>
                }
            </Card>
            {selected != null &&
                <div className="d-flex justify-content-between mb-3">
                    <button className="btn btn-outline-soft mt-3" disabled={existingData == null || redirectChecking} onClick={c => { c.preventDefault(); CancelApplying(); }}>Cancel</button>
                    {app.loggedInUser != null && app.loggedInUser.UserType == UserType.Candidate && <button className="btn btn-alt mt-3" disabled={existingData == null || redirectChecking || alreadyApplied != null} onClick={c => { c.preventDefault(); Apply(); }}>{existingToCopy == null ? "Apply" : "Apply and copy existing"}</button>}
                    {app.loggedInUser != null && (app.loggedInUser.UserType == UserType.Agency || app.loggedInUser.UserType == UserType.Admin) && ((a?.length ?? 0) > 0 || (aref?.length ?? 0) > 0) && <button className="btn btn-alt mt-3" disabled={existingData == null || redirectChecking} onClick={c => { c.preventDefault(); Apply(); }}>Put {n} candidate forward for this position</button>}
                    {app.loggedInUser != null && (app.loggedInUser.UserType == UserType.Agency || app.loggedInUser.UserType == UserType.Admin) && <button className="btn btn-alt mt-3" disabled={existingData == null || redirectChecking} onClick={c => { c.preventDefault(); Apply(); }}>Put a candidate forward for this position</button>}
                </div>
            }
        </>
    );
}